import type { MaterialBundle, MaterialUnit } from '../types/nurseryMaterial';

/**
 * A scanned material label: `MAT:<id>`. Anything else is not one (a lot label
 * says LOT:, a barcode is digits).
 */
export function parseMaterialScan(raw: string): number | null {
  const m = /^MAT:(\d+)$/i.exec(raw.trim());
  return m ? Number(m[1]) : null;
}

/** "120 buds", "1.5 grams": whole numbers without decimals, the rest to two places at most. */
export function formatQuantity(q: unknown, unit?: MaterialUnit | string | null): string {
  // Number(null) and Number('') are 0: a missing quantity must not read as none.
  const n = q === null || q === undefined || q === '' ? NaN : Number(q);
  // Grouped by hand: the test runner's ICU may not carry en-AU.
  const group = (s: string) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  let text = '—';
  if (Number.isFinite(n)) {
    const [whole, frac] = String(Math.round(n * 100) / 100).split('.');
    text = group(whole) + (frac ? `.${frac}` : '');
  }
  return unit ? `${text} ${unit}` : text;
}

/** Where a bundle came from, in words. */
export function materialSource(m: Pick<MaterialBundle, 'source_type' | 'source_lot_id' | 'source_lot_code'
  | 'source_cultivar_area_name' | 'source_farm_name' | 'supplier_name' | 'source_text'>): string {
  if (m.source_type === 'lot') return m.source_lot_code || `Lot #${m.source_lot_id}`;
  if (m.source_type === 'cultivar_area') {
    return [m.source_farm_name, m.source_cultivar_area_name].filter(Boolean).join(' · ') || 'An orchard planting';
  }
  if (m.source_type === 'supplier') return m.supplier_name || m.source_text || 'A supplier';
  return m.source_text || '—';
}

/**
 * Seed bought by weight, in seeds: grams × seeds per kg ÷ 1000. Null when
 * the bundle is counted in seeds already or carries no seeds per kg.
 */
export function seedsInBundle(m: Pick<MaterialBundle, 'kind' | 'unit' | 'remaining' | 'seeds_per_kg'>): number | null {
  if (m.kind !== 'seed' || m.unit !== 'grams' || m.seeds_per_kg == null) return null;
  return Math.round((Number(m.remaining) * Number(m.seeds_per_kg)) / 1000);
}
